import React, { memo, useMemo } from 'react';
import styled from 'styled-components';

import { formatPricePerHour, formatPrice, formatDuration } from '@front/common/src/utils/formatting';
import { renderActivities, renderChildren } from '@front/common/src/utils/childcare';
import colors from '@front/common/src/utils/colors';
import { ChildcareBasketPrices } from '@front/common/src/types/commons';

import { translate } from '@front/i18n';

import Accordion from '@front/web-ui/src/components/accordion/accordion';
import Separator from '@front/web-ui/src/components/separator/separator';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import PromoCode from '../../../../promo-code/promo-code';
import { ContentLine, CategoryLine, SurchargeTitle, SurchargeLine } from '../../../common';
import HourlyCalculationPrices from '../../hourly-calculation-prices';
import {
  route,
  PRESTATIONS_PATH,
  SPOT_APPOINTMENT_PATH,
  CHILDREN_PATH,
  ACTIVITIES_PATH,
} from '../../../../../steps/steps';
import useBasketSection from '../../../../../hooks/use-basket-section';
import ServiceFeeIncludedText from '../../../service-fee-included-text';
import type { ChildcareBasketProps } from '../../childcare-basket';

import AllowanceInfo from './allowance-info';

const t = translate('basket');

const StyledAccordion = styled(Accordion)`
  margin: 20px 0;
`;

type SpotPrestationsProps = Pick<
  ChildcareBasketProps,
  'promotion' | 'prestation' | 'duration' | 'activities' | 'childrenCount' | 'user' | 'shouldShowPromoCode'
> & {
  prices: ChildcareBasketPrices;
};

const SpotPrestations = ({
  promotion,
  prestation,
  duration,
  prices,
  activities,
  childrenCount,
  user,
  shouldShowPromoCode,
}: SpotPrestationsProps) => {
  const {
    appointmentSurcharge,
    appointmentSurchargeReason,
    hourlyPrice,
    hourlyPriceSurcharge,
    totalPrice,
    giftValue,
  } = prices;
  const durationInHours = duration ? formatDuration(duration) : undefined;
  const hasAppointmentSurcharge = !!appointmentSurcharge && appointmentSurcharge > 0;

  const renderedActivities = useMemo(() => renderActivities(activities), [activities]);

  const accordionTitle = duration
    ? t(`.childcare.prestation_with_duration.${prestation}`, { duration: durationInHours })
    : t(`.childcare.prestation.${prestation}`);
  const hourlyPriceFormatted = formatPricePerHour(hourlyPrice);
  const totalPriceFormatted = totalPrice ? formatPrice(totalPrice) : undefined;

  // Always show the total price for gift cards, even if there is a total of 0
  const hasTotalPrice = !!totalPrice || !!giftValue;

  const { isOpen } = useBasketSection({
    data: !!prestation,
    sectionLocations: [
      route(PRESTATIONS_PATH),
      route(SPOT_APPOINTMENT_PATH),
      route(CHILDREN_PATH),
      route(ACTIVITIES_PATH),
    ],
  });

  return (
    <>
      <Separator />
      <StyledAccordion
        icon='basket'
        title={accordionTitle}
        subtitle={<Text.SM>{hourlyPriceFormatted}</Text.SM>}
        titlePreview={totalPriceFormatted}
        isOpen={isOpen}
      >
        {childrenCount && <ContentLine>{renderChildren(childrenCount)}</ContentLine>}
        <ContentLine>{renderedActivities}</ContentLine>
        {hasAppointmentSurcharge && (
          <CategoryLine>
            <SurchargeTitle display='block'>{t('.appointment_surcharge.title')}</SurchargeTitle>
            <SurchargeLine>
              <Text>{t(`.appointment_surcharge.${appointmentSurchargeReason}`)}</Text>
              <Text.SM color={colors.ink50}>{formatPrice(hourlyPriceSurcharge)}</Text.SM>
            </SurchargeLine>
          </CategoryLine>
        )}
        <HourlyCalculationPrices prices={prices} duration={duration} promotion={promotion} />
        <ServiceFeeIncludedText />
        <AllowanceInfo />
        {hasTotalPrice && shouldShowPromoCode && (
          <PromoCode
            universeReference='childcare'
            promotion={promotion}
            hasGift={false} // gift card is not supported yet for childcare universe
            totalPrice={totalPrice}
            user={user}
          />
        )}
      </StyledAccordion>
    </>
  );
};

export default memo(SpotPrestations);
